import { useQuery, useQueryClient } from "react-query";
import { useCallback, useEffect, useRef } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "@/services/functions";
import { useStore } from "@/stores/useStore";
import { ContainerTimeConfig } from "@/types/v2/Metrics";
import {
  ContainerTimeMetric,
  SerializedContainerTimeMetric,
} from "@/types/v2/PrecomputedMetrics";
import { firestore } from "@/services/firestore";

interface ComputeContainerTimeMetricRequest {
  shopId: string;
  configuration: Omit<
    ContainerTimeConfig,
    "id" | "name" | "createdAt" | "updatedAt" | "createdBy"
  > & {
    dateRangeStart: number; // timestamp
    dateRangeEnd: number; // timestamp
  };
}

interface ComputeContainerTimeMetricResponse {
  cacheKey: string;
  metric: SerializedContainerTimeMetric | null;
  cached: boolean;
  isStale: boolean;
  backgroundRefresh: boolean;
  computationTimeMs: number;
}

/**
 * Custom hook for fetching container time metrics with intelligent caching
 * Integrates with cloud functions and provides real-time cache invalidation
 * @param config
 * @param config.firstContainer
 * @param config.firstEvent
 * @param config.secondContainer
 * @param config.secondEvent
 * @param config.timeUnit
 * @param config.colorCoding
 * @param config.chartType
 * @param config.dateRangeStart
 * @param config.dateRangeEnd
 */
export function useContainerTimeMetrics(
  config: Pick<
    ContainerTimeConfig,
    | "firstContainer"
    | "firstEvent"
    | "secondContainer"
    | "secondEvent"
    | "timeUnit"
    | "colorCoding"
    | "chartType"
  > & {
    dateRangeStart: Date;
    dateRangeEnd: Date;
  }
) {
  const { shop } = useStore();
  const queryClient = useQueryClient();
  const shopId = shop?.id;
  const lastCacheKeyRef = useRef<string | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  // Convert dates to timestamps for the API
  const requestConfig = {
    ...config,
    dateRangeStart: config.dateRangeStart.getTime(),
    dateRangeEnd: config.dateRangeEnd.getTime(),
  };

  // Generate stable query key
  const queryKey = ["containerTimeMetrics", shopId, requestConfig];

  // Create the callable function outside of query to avoid recreating
  const computeMetricCallable = useCallback(() => {
    return httpsCallable<
      ComputeContainerTimeMetricRequest,
      ComputeContainerTimeMetricResponse
    >(functions, "functionsV2-computeContainerTimeMetric");
  }, []);

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      if (!shopId) throw new Error("Shop ID not available");

      const callable = computeMetricCallable();
      const result = await callable({
        shopId,
        configuration: requestConfig,
      });

      return result.data;
    },
    enabled: !!shopId && !!config.firstContainer && !!config.secondContainer,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 60, // 1 hour - consider fresh for 1 hour
    cacheTime: 1000 * 60 * 60 * 24, // 24 hours - keep in memory for 24 hours
  });

  // Set up a real-time cache invalidation listener only when cacheKey changes
  useEffect(() => {
    const currentCacheKey = query.data?.cacheKey;

    // Only set up a new subscription if cacheKey actually changed
    if (
      !shopId ||
      !currentCacheKey ||
      currentCacheKey === lastCacheKeyRef.current
    ) {
      return;
    }

    // Clean up previous subscription
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    const metricPath = `shops/${shopId}/containerTimeMetrics/${currentCacheKey}`;
    let lastStaleState: boolean | undefined = undefined;

    unsubscribeRef.current = firestore.subscribeDocument<ContainerTimeMetric>(
      metricPath,
      (snapshot) => {
        if (snapshot) {
          const metricData = snapshot as ContainerTimeMetric;
          const currentStaleState = metricData.cache.isStale;

          // Only invalidate if cache became newly stale (transition from false/undefined to true)
          // This prevents loops from age-based staleness that doesn't require invalidation
          if (currentStaleState && lastStaleState === false) {
            // Use setTimeout to avoid immediate refetch during render
            setTimeout(() => {
              queryClient.invalidateQueries(queryKey);
            }, 100);
          }

          lastStaleState = currentStaleState;
        }
      }
    );
    lastCacheKeyRef.current = currentCacheKey;

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [shopId, query.data?.cacheKey, queryClient]); // Minimal deps to prevent loops

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, []);

  return {
    ...query,
    metric: query.data?.metric,
    cached: query.data?.cached,
    isStale: query.data?.isStale,
    backgroundRefresh: query.data?.backgroundRefresh,
    computationTimeMs: query.data?.computationTimeMs,
    computedAt: query.data?.metric?.cache?.computedAt,
    cacheKey: query.data?.cacheKey,
  };
}

/**
 * Query factory for consistent query key generation
 * @param shopId
 * @param config
 */
export const containerTimeMetricsQuery = (
  shopId: string,
  config: ContainerTimeConfig & {
    dateRangeStart: number;
    dateRangeEnd: number;
  }
) => ({
  queryKey: ["containerTimeMetrics", shopId, config],
  queryFn: async () => {
    const computeMetricCallable = httpsCallable<
      ComputeContainerTimeMetricRequest,
      ComputeContainerTimeMetricResponse
    >(functions, "functionsV2-computeContainerTimeMetric");

    const result = await computeMetricCallable({
      shopId,
      configuration: config,
    });

    return result.data;
  },
  refetchOnWindowFocus: false,
  staleTime: 1000 * 60 * 60, // 1 hour
  cacheTime: 1000 * 60 * 60 * 24, // 24 hours
});
